/**
 * Bulk Operations Utilities
 *
 * Functions for performing bulk operations on multiple users.
 * All user document writes go through API (users-admin-api.ts).
 */

import { UserRole } from '../types/rbac';
import {
  assignApiRole,
  removeApiRole,
} from '@/lib/role-operations-api';
import {
  bulkDeleteUsersApi,
} from '@/lib/users-admin-api';
import { migrateEmailKeyApi, restoreEmailKeyApi } from '@/components/authnz/services/users-api';
import { requestAdminSendSignInEmail } from '@/lib/sign-in-link-api';

export interface BulkOperationResult {
  success: number;
  failed: number;
  errors: Array<{ userId: string; error: string }>;
}

/**
 * Bulk add roles to multiple users
 */
export async function bulkAddRoles(
  userIds: string[],
  role: UserRole,
  assignedBy: string,
  userEmails: Map<string, string>
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const userId of userIds) {
    try {
      const email = userEmails.get(userId) || '';
      const success = await assignApiRole(email ? null : userId, email, role, assignedBy);
      if (success) {
        result.success++;
      } else {
        throw new Error('API assignment failed');
      }
    } catch (error: any) {
      result.failed++;
      result.errors.push({
        userId,
        error: error.message || 'Unknown error',
      });
    }
  }

  return result;
}

/**
 * Bulk remove roles from multiple users
 */
export async function bulkRemoveRoles(
  userIds: string[],
  role: UserRole,
  userEmails?: Map<string, string>,
  options?: { confirmSelf?: boolean; uidByUserId?: Map<string, string> }
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const userId of userIds) {
    try {
      const email = userEmails?.get(userId);
      const targetUserId = options?.uidByUserId?.get(userId) ?? userId;
      const success = await removeApiRole(targetUserId, role, email, {
        confirmSelf: options?.confirmSelf,
      });
      if (success) {
        result.success++;
      } else {
        throw new Error('API removal failed');
      }
    } catch (error: any) {
      result.failed++;
      result.errors.push({
        userId,
        error: error.message || 'Unknown error',
      });
    }
  }

  return result;
}

/**
 * Bulk set primary role for multiple users
 */
export async function bulkSetPrimaryRole(
  userIds: string[],
  primaryRole: UserRole,
  assignedBy: string,
  userEmails: Map<string, string>
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const userId of userIds) {
    try {
      const email = userEmails.get(userId) || '';
      const success = await assignApiRole(userId, email, primaryRole, assignedBy);
      if (success) {
        result.success++;
      } else {
        throw new Error('API assignment failed');
      }
    } catch (error: any) {
      result.failed++;
      result.errors.push({
        userId,
        error: error.message || 'Unknown error',
      });
    }
  }

  return result;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

async function mapEmailKeys(
  emailKeys: string[],
  run: (emailKey: string) => Promise<void>,
): Promise<BulkOperationResult> {
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: [],
  };
  for (const emailKey of emailKeys) {
    try {
      await run(emailKey);
      result.success += 1;
    } catch (error: unknown) {
      result.failed += 1;
      result.errors.push({ userId: emailKey, error: errorMessage(error) });
    }
  }
  return result;
}

export async function bulkMigrateEmailKeys(emailKeys: string[]): Promise<BulkOperationResult> {
  return mapEmailKeys(emailKeys, async (emailKey) => {
    const result = await migrateEmailKeyApi(emailKey);
    if (!result.ok) {
      throw new Error(result.message);
    }
  });
}

export async function bulkRestoreEmailKeys(emailKeys: string[]): Promise<BulkOperationResult> {
  return mapEmailKeys(emailKeys, (emailKey) => restoreEmailKeyApi(emailKey));
}

export async function bulkSendSignInEmails(emailKeys: string[]): Promise<BulkOperationResult> {
  return mapEmailKeys(emailKeys, async (emailKey) => {
    const result = await requestAdminSendSignInEmail(emailKey);
    if (!result.success) {
      throw new Error(result.message || 'Send sign-in email failed');
    }
  });
}

/**
 * Bulk soft delete users (via API)
 */
export async function bulkDeleteUsers(
  userIds: string[],
  deletedBy: string,
  options?: { confirmSelf?: boolean }
): Promise<BulkOperationResult> {
  return bulkDeleteUsersApi(userIds, deletedBy, options);
}

/**
 * Process bulk operations in batches to avoid timeouts
 */
export async function processBulkOperation<T>(
  items: T[],
  batchSize: number,
  operation: (batch: T[]) => Promise<BulkOperationResult>
): Promise<BulkOperationResult> {
  const totalResult: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResult = await operation(batch);

    totalResult.success += batchResult.success;
    totalResult.failed += batchResult.failed;
    totalResult.errors.push(...batchResult.errors);
  }

  return totalResult;
}

